/**
 * useUpdatableFields — advisory list of fields that can be targeted by a
 * bulk-update action for a given entity type.
 *
 * Combines the property schema and association types for the org and returns
 * the subset that passes the write-access / cardinality rules the bulk-update
 * API enforces.  The backend is still authoritative (it will 400 for anything
 * it rejects), so callers should treat this list as advisory, not a guarantee.
 */

import { useMemo } from 'react';

import type { AssociationType } from '../core/SchemaClient';
import { getEntityTypeConfig } from '../record/registry/entityTypeRegistry';
import {
  DISPLAY_NAME_FIELDS,
  HIDDEN_ASSOCIATION_TARGETS,
  SYSTEM_FIELDS,
} from '../record/registry/fieldConstants';
import {
  associationMatchesSource,
  buildEntityKindMap,
  getEntityKinds,
  isAssociationVisibleOnOverview,
} from '../utils/associationMatching';
import { useAssociationTypes } from './useAssociationTypes';
import { useObjectTypes } from './useObjectTypes';
import {
  type PropertyTypeSchema,
  usePropertySchema,
} from './usePropertySchema';
import { useStageTypeNames } from './useStageTypeNames';

export interface UpdatablePropertyField {
  kind: 'property';
  name: string;
  label: string;
  field_type_name: string;
  schema: PropertyTypeSchema;
}

export interface UpdatableAssociationField {
  kind: 'association';
  name: string;
  label: string;
  targetEntityTypeName: string;
  cardinality: 'one_to_one' | 'many_to_one' | 'many_to_many';
}

export type UpdatableField = UpdatablePropertyField | UpdatableAssociationField;

export interface UseUpdatableFieldsOptions {
  /**
   * Allow `many_to_many` associations into the result. Multi-value associations
   * are edited as a set (add/remove diff), which the single-entity inline edit
   * surface supports but the bulk-update path does not (its update-association
   * endpoint takes one target id and overwrites). Defaults to `false` so the
   * bulk-update modal keeps excluding them; the inline highlight-card edit opts
   * in.
   */
  includeMultiValueAssociations?: boolean;
}

export interface UseUpdatableFieldsResult {
  fields: UpdatableField[];
  isLoading: boolean;
}

function isEligibleAssociation(
  at: AssociationType,
  includeMultiValueAssociations: boolean
): at is AssociationType & {
  cardinality: 'one_to_one' | 'many_to_one' | 'many_to_many';
} {
  if (at.value_write_access !== 'all') {
    return false;
  }
  if (at.cardinality === 'one_to_one' || at.cardinality === 'many_to_one') {
    return true;
  }
  return at.cardinality === 'many_to_many' && includeMultiValueAssociations;
}

/**
 * Returns the union of bulk-updatable property fields and association fields
 * for the given entity type, grouped by kind.
 *
 * The goal is for this list to be a subset of what is editable from the record
 * overview's edit mode — if the user can't edit a field there, it must not be
 * offered for bulk update. These rules mirror overview-edit-section's
 * editableAssociationTypes filter and buildDetailFieldList's edit-mode property
 * rules.
 *
 * Property eligibility:
 *  - not a SYSTEM_FIELD, DISPLAY_NAME_FIELD, or config.hiddenProperties entry
 *    (mirrors record-slideout visibility — if it's hidden there, it's hidden here)
 *  - value_read_access and value_write_access must both be 'all' (matches the
 *    overview edit list)
 *  - is_unique_value must be false (unique constraints prevent bulk-setting)
 *  - value_update_access must be 'all' (backend bulk-update authority)
 *
 * Association eligibility:
 *  - source matches entityTypeName (type-based or kind-based via entity_kind map)
 *  - cardinality is one_to_one or many_to_one — or many_to_many when the caller
 *    passes `includeMultiValueAssociations` (the single-entity inline edit path,
 *    which writes a set add/remove diff; bulk-update can't express that, so it
 *    leaves the option off)
 *  - value_write_access is 'all' (stage associations satisfy this)
 *  - value_read_access is 'all'
 *  - value_removal_access is not 'none' (write-once associations can't be
 *    bulk-set — see the inline comment)
 *  - target is not a HIDDEN_ASSOCIATION_TARGET (notes, files, meetings, etc. that
 *    have dedicated record-view sections rather than overview rows)
 *  - target passes the kind predicate (isAssociationVisibleOnOverview — visible
 *    unless the target's kind is in HIDDEN_TARGET_KINDS: activity, event, reporting)
 */
export function useUpdatableFields(
  orgId: number,
  entityTypeName: string,
  options?: UseUpdatableFieldsOptions
): UseUpdatableFieldsResult {
  const includeMultiValueAssociations =
    options?.includeMultiValueAssociations ?? false;
  const { schema, loading: propertiesLoading } = usePropertySchema(
    orgId,
    entityTypeName
  );
  const { associationTypes, isLoading: associationTypesLoading } =
    useAssociationTypes(orgId);
  const { isLoading: stageTypesLoading } = useStageTypeNames(orgId);
  const objectTypesQuery = useObjectTypes(orgId);

  const entityKindMap = useMemo(
    () => buildEntityKindMap(objectTypesQuery.data?.allObjectTypes ?? []),
    [objectTypesQuery.data?.allObjectTypes]
  );

  const fields = useMemo<UpdatableField[]>(() => {
    const result: UpdatableField[] = [];

    // Mirror the record slideout's visibility config so the bulk-update list
    // only offers fields the user already sees there: anything hidden, system,
    // or rendered only in the record header is excluded.
    const config = getEntityTypeConfig(entityTypeName);
    const hiddenPropertySet = new Set(config.hiddenProperties ?? []);

    for (const prop of Object.values(schema)) {
      const propName = prop.property_type_name;
      if (SYSTEM_FIELDS.has(propName)) {
        continue;
      }
      if (DISPLAY_NAME_FIELDS.has(propName)) {
        continue;
      }
      if (hiddenPropertySet.has(propName)) {
        continue;
      }
      // Mirror overview-edit property visibility: only fully readable +
      // writable properties are editable there.
      if (prop.value_read_access !== 'all') {
        continue;
      }
      if (prop.value_write_access !== 'all') {
        continue;
      }
      if (prop.is_unique_value) {
        continue;
      }
      if (prop.value_update_access !== 'all') {
        continue;
      }
      result.push({
        kind: 'property',
        name: propName,
        label: prop.label,
        field_type_name: prop.field_type_name,
        schema: prop,
      });
    }

    const entityKinds = getEntityKinds(entityTypeName, entityKindMap);
    for (const assoc of associationTypes) {
      if (!associationMatchesSource(assoc, entityTypeName, entityKinds)) {
        continue;
      }
      if (!isEligibleAssociation(assoc, includeMultiValueAssociations)) {
        continue;
      }
      // Skip association targets that have dedicated record-view sections
      // (notes, files, meetings, etc.) — same rule as buildDetailFieldList.
      // WORKAROUND until #12411: see fieldConstants.ts HIDDEN_ASSOCIATION_TARGETS
      if (HIDDEN_ASSOCIATION_TARGETS.has(assoc.target_entity_type_name)) {
        continue;
      }
      if (!isAssociationVisibleOnOverview(assoc, entityKindMap)) {
        continue;
      }
      if (assoc.value_read_access !== 'all') {
        continue;
      }
      // Write-once associations are excluded outright, not per-record: a bulk
      // update applies one value to every selected record, and the backend
      // rejects it for any record that already has a target. Offering the field
      // could only produce a partial failure. (The single-record surfaces gate
      // per-row instead — see associationEditability.) Mirrors the
      // value_update_access exclusion for properties above.
      if (assoc.value_removal_access === 'none') {
        continue;
      }
      result.push({
        kind: 'association',
        name: assoc.association_type_name,
        label: assoc.label,
        targetEntityTypeName: assoc.target_entity_type_name,
        cardinality: assoc.cardinality,
      });
    }

    return result;
  }, [
    schema,
    associationTypes,
    entityTypeName,
    entityKindMap,
    includeMultiValueAssociations,
  ]);

  return {
    fields,
    isLoading:
      propertiesLoading ||
      associationTypesLoading ||
      stageTypesLoading ||
      objectTypesQuery.isLoading,
  };
}
